// src/pages/TraceToggle/TraceToggle.tsx
import React from "react";
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    Typography,
    Switch,
    FormControlLabel,
    IconButton,
    Alert,
    CircularProgress,
} from "@mui/material";
import { KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import { useAuth } from "../../auth/AuthContext";
import { hasNewRelicKey, fetchEntities } from "../../utils/newRelicDataUtil";
import { useEntityOrder } from "../../utils/useEntityOrder";

export default function TraceToggle() {
    const { getAccessToken } = useAuth();

    const [hasKey, setHasKey] = React.useState<boolean | null>(null);
    const [entities, setEntities] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [toggles, setToggles] = React.useState<Record<string, boolean>>({});

    const { orderedEntities, moveUp, moveDown } = useEntityOrder(entities, "trace.toggle.order");

    // Load New Relic key + entities
    React.useEffect(() => {
        (async () => {
            const token = await getAccessToken?.();
            const ok = token ? await hasNewRelicKey(token) : false;
            setHasKey(ok);
            if (!ok) {
                setLoading(false);
                return;
            }

            const raw = await fetchEntities(token!);
            const ents = raw?.data?.actor?.entitySearch?.results?.entities ?? [];
            setEntities(ents);

            // Initialize toggle state from localStorage (persisted per entity)
            const saved = localStorage.getItem("trace.toggle.state");
            if (saved) {
                setToggles(JSON.parse(saved));
            }

            setLoading(false);
        })();
    }, [getAccessToken]);

    const handleToggle = (guid: string) => {
        const newState = { ...toggles, [guid]: !toggles[guid] };
        setToggles(newState);
        localStorage.setItem("trace.toggle.state", JSON.stringify(newState));
        // TODO: call backend when ready
    };

    if (loading) {
        return (
            <Box sx={{ m: 3, textAlign: "center" }}>
                <CircularProgress />
                <Typography>Loading entities…</Typography>
            </Box>
        );
    }

    if (!hasKey) {
        return (
            <Alert severity="warning" sx={{ m: 3 }}>
                Connect your New Relic account to manage trace ingestion.
            </Alert>
        );
    }

    return (
        <Box sx={{ m: 3, maxWidth: 700 }}>
            <Card variant="outlined">
                <CardHeader
                    title="Trace Ingestion Toggle"
                    subheader="Enable or disable distributed tracing per service"
                />

                <Alert severity="info" sx={{ mx: 2, mt: 2 }}>
                    Currently in preview; toggles are not functional. Full backend integration coming soon.
                </Alert>

                <CardContent>
                    {orderedEntities.length === 0 ? (
                        <Typography color="text.secondary">No services found</Typography>
                    ) : (
                        orderedEntities.map((entity: any, i: number) => (
                            <Box
                                key={entity.guid}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    py: 1.5,
                                    borderBottom: i < orderedEntities.length - 1 ? "1px solid" : "none",
                                    borderColor: "divider",
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!toggles[entity.guid]}
                                            onChange={() => handleToggle(entity.guid)}
                                            color="primary"
                                        />
                                    }
                                    label={entity.name}
                                    labelPlacement="start"
                                    sx={{ m: 0, flexGrow: 1 }}
                                />

                                <Box sx={{ display: "flex" }}>
                                    <IconButton
                                        onClick={() => moveUp(entity.guid)}
                                        disabled={i === 0}
                                        size="small"
                                    >
                                        <KeyboardArrowUp />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => moveDown(entity.guid)}
                                        disabled={i === orderedEntities.length - 1}
                                        size="small"
                                    >
                                        <KeyboardArrowDown />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))
                    )}
                </CardContent>
            </Card>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: "block" }}>
                Order and toggle state are saved per user in your browser.
            </Typography>
        </Box>
    );
}